import { triggerPostMoveFlash } from '@atlaskit/pragmatic-drag-and-drop-flourish/trigger-post-move-flash';
import {
  type Edge,
  extractClosestEdge
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { getReorderDestinationIndex } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Stack, xcss } from '@atlaskit/primitives';
import { debounce } from 'lodash-es';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import ListItem, { itemKey } from './ListItem.tsx';
import { type ItemEntry, ListContext, type ListContextValue } from './useListContext.ts';

type ItemPosition = 'first' | 'last' | 'middle' | 'only';

const listItemContainerStyles = xcss({
  listStyleType: 'none',
  padding: 'space.0',
  display: 'flex',
  flexDirection: 'column'
});

type ItemData<T> = {
  [itemKey]: true;
  item: T;
  index: number;
  instanceId: symbol;
};

function isItemData<T>(data: Record<string | symbol, unknown>): data is ItemData<T> {
  return data[itemKey] === true;
}

interface ItemPositionParams<T> {
  index: number;
  items: T[];
}

function getItemPosition<T>({ index, items }: ItemPositionParams<T>): ItemPosition {
  if (items.length === 1) {
    return 'only';
  }

  if (index === 0) {
    return 'first';
  }

  if (index === items.length - 1) {
    return 'last';
  }

  return 'middle';
}

export interface ListItemProps<T, U> {
  item: T;
  index: number;
  position: ItemPosition;
  renderField: (data: {
    i: number;
    value: T;
    autoFocus: boolean;
    onChange: (value: T, delta: U) => void;
    isReadonly: boolean;
    isDisabled: boolean;
  }) => React.ReactNode;
  onReorder?: (from: number, to: number) => void;
  onValueChange?: (i: number, value: T, delta: U) => void;
  isDisabled?: boolean;
  isReadonly?: boolean;
  autoFocus?: number | 'addButton';
  noDelete?: boolean;
  onDelete?: (index: number) => void;
  activeId?: string;
  editingId?: string;
  errorId?: string;
  setListState: React.Dispatch<React.SetStateAction<ListState<T>>>;
  renderDragImage?: (value: T) => React.ReactNode;
  showHoverBackground?: boolean;
  alignContentStart?: boolean;
}

function getItemRegistry() {
  const registry = new Map<string, HTMLElement>();

  function register({ itemId, element }: ItemEntry) {
    registry.set(itemId, element);

    return function unregister() {
      registry.delete(itemId);
    };
  }

  function getElement(itemId: string): HTMLElement | null {
    return registry.get(itemId) ?? null;
  }

  return { register, getElement };
}

type ListState<T> = {
  items: T[];
  lastCardMoved: {
    item: T;
    dragHandleRef?: React.RefObject<HTMLButtonElement>;
  } | null;
};

export interface DraggableListProps<T, U> {
  onReorder?: (from: number, to: number) => void;
  onValueChange?: (i: number, value: T, delta: U) => void;
  renderField: (data: {
    i: number;
    value: T;
    autoFocus: boolean;
    onChange: (value: T, delta: U) => void;
    isReadonly: boolean;
    isDisabled: boolean;
  }) => React.ReactNode;
  values: T[];
  getId: (value: T) => string;
  isDisabled?: boolean;
  isReadonly?: boolean;
  autoFocus?: number | 'addButton';
  noDelete?: boolean;
  deleteTooltip?: string;
  onDelete?: (index: number) => void;
  activeId?: string;
  editingId?: string;
  errorId?: string;
  duplicateId?: string | undefined;
  isHovering?: boolean;
  renderDragImage?: (value: T) => React.ReactNode;
  showHoverBackground?: boolean;
  getItemLabel?: (value: T) => string;
  compactDeleteButton?: boolean;
  alignContentStart?: boolean;
}

export function DraggableList<T, U>({
  onReorder,
  onValueChange,
  renderField,
  getId,
  values,
  isDisabled,
  isReadonly,
  autoFocus,
  noDelete,
  deleteTooltip,
  onDelete,
  activeId,
  errorId,
  editingId,
  isHovering,
  renderDragImage,
  duplicateId,
  showHoverBackground = false,
  getItemLabel,
  compactDeleteButton = false,
  alignContentStart = false
}: DraggableListProps<T, U>): React.ReactElement {
  const [{ items, lastCardMoved }, setListState] = useState<ListState<T>>({
    items: values,
    lastCardMoved: null
  });

  const [hasInteractedWithList, setHasInteractedWithList] = useState(false);
  const onListInteraction = useCallback(() => setHasInteractedWithList(true), []);

  const [registry] = useState(getItemRegistry);

  const reorderItem = useCallback(
    ({
      startIndex,
      indexOfTarget,
      closestEdgeOfTarget
    }: {
      startIndex: number;
      indexOfTarget: number;
      closestEdgeOfTarget: Edge | null;
    }) => {
      const finishIndex = getReorderDestinationIndex({
        startIndex,
        closestEdgeOfTarget,
        indexOfTarget,
        axis: 'vertical'
      });
      if (finishIndex === startIndex) {
        return;
      }
      onReorder?.(startIndex, finishIndex);
      setListState((state) => ({
        ...state,
        lastCardMoved: {
          item: items[startIndex]
        }
      }));
    },
    [items, onReorder]
  );

  useEffect(() => {
    setListState((state) => ({
      ...state,
      items: values
    }));

    const debouncedFocus = debounce(() => {
      if (lastCardMoved === null) {
        return;
      }

      const element = registry.getElement(getId(lastCardMoved.item));
      if (element) {
        triggerPostMoveFlash(element);
        setListState((state) => ({
          ...state,
          lastCardMoved: null
        }));
      }
    }, 0);
    debouncedFocus();
    return () => {
      debouncedFocus.cancel();
    };
  }, [getId, lastCardMoved, registry, values]);

  const [instanceId] = useState(() => Symbol('instance-id'));

  useEffect(() => {
    return monitorForElements({
      canMonitor({ source }) {
        return isItemData(source.data) && source.data.instanceId === instanceId;
      },
      onDrop({ location, source }) {
        const target = location.current.dropTargets[0];
        const targetData = target?.data;
        if (!target) {
          return;
        }

        if (!isItemData(source.data) || !isItemData(target.data)) {
          return;
        }

        if (target.data.index < 0) {
          return;
        }

        const closestEdgeOfTarget = extractClosestEdge(targetData);
        reorderItem({
          startIndex: source.data.index,
          indexOfTarget: target.data.index,
          closestEdgeOfTarget
        });
      }
    });
  }, [instanceId, items, onReorder, reorderItem, values]);

  const getListLength = useCallback(() => items.length, [items.length]);

  const contextValue: ListContextValue = useMemo(() => {
    return {
      registerItem: registry.register,
      instanceId,
      getListLength
    };
  }, [registry.register, instanceId, getListLength]);

  const listItems = useMemo(() => {
    return items.map((item, index) => {
      const isDisabledByDuplicateCheck = duplicateId
        ? getId?.(item) !== duplicateId
        : false;
      return (
        <ListItem
          data-interactive
          key={getId(item)}
          item={item}
          itemId={getId(item)}
          index={index}
          position={getItemPosition({ index, items })}
          renderField={renderField}
          onReorder={onReorder}
          onValueChange={onValueChange}
          isDisabled={isDisabled || isDisabledByDuplicateCheck}
          duplicateId={duplicateId}
          isReadonly={isReadonly}
          autoFocus={autoFocus}
          noDelete={noDelete}
          deleteTooltip={deleteTooltip}
          onDelete={onDelete}
          activeId={activeId}
          errorId={errorId}
          editingId={editingId}
          setListState={setListState}
          isHovering={isHovering}
          renderDragImage={renderDragImage}
          showHoverBackground={showHoverBackground}
          getItemLabel={getItemLabel}
          hasInteractedWithList={showHoverBackground ? hasInteractedWithList : undefined}
          onListInteraction={showHoverBackground ? onListInteraction : undefined}
          compactDeleteButton={compactDeleteButton}
          alignContentStart={alignContentStart}
        />
      );
    });
  }, [
    items,
    deleteTooltip,
    duplicateId,
    getId,
    renderField,
    onReorder,
    onValueChange,
    isDisabled,
    isReadonly,
    autoFocus,
    noDelete,
    onDelete,
    activeId,
    errorId,
    editingId,
    isHovering,
    renderDragImage,
    showHoverBackground,
    getItemLabel,
    compactDeleteButton,
    alignContentStart,
    hasInteractedWithList,
    onListInteraction
  ]);

  return (
    <ListContext.Provider value={contextValue}>
      <Stack as="ul" xcss={listItemContainerStyles} testId="draggable-list-items">
        {listItems}
      </Stack>
    </ListContext.Provider>
  );
}
